/**
 * Agent Tool Registry — read-only wrappers around existing admin endpoints.
 *
 * Every tool hits an endpoint that already runs through SIWE + withAdmin(),
 * so no new backend surface is introduced. Tools are:
 *   - registered with navigator.modelContext (WebMCP) when available, for
 *     future LLMs that can auto-invoke them
 *   - invokable directly via executeTool() for the in-browser agent loop
 *     that parses a "<<TOOL: name args>>" convention out of Gemini Nano's text
 */

export interface AgentTool {
  name: string
  description: string
  inputSchema: {
    type: 'object'
    properties: Record<string, { type: string; description?: string; default?: unknown }>
    required?: string[]
  }
  execute: (args: Record<string, unknown>) => Promise<unknown>
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } }
      | null
    throw new Error(body?.error?.message || `HTTP ${res.status}`)
  }
  const json = (await res.json()) as { data?: unknown }
  return json.data ?? json
}

function numberOr(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

export const TOOLS: AgentTool[] = [
  {
    name: 'get_dashboard_stats',
    description: 'Fetch headline admin metrics: total revenue, orders, products, buyers.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => fetchJson('/api/admin/stats'),
  },
  {
    name: 'list_recent_orders',
    description: 'List the most recent orders with status and amount.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max orders to return (default 10)', default: 10 },
      },
    },
    execute: async ({ limit }) => fetchJson(`/api/admin/orders?limit=${numberOr(limit, 10)}`),
  },
  {
    name: 'get_treasury_overview',
    description: 'Fetch treasury totals — revenue by chain and monthly breakdown.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => fetchJson('/api/admin/treasury'),
  },
  {
    name: 'list_products',
    description: 'List products in the catalog with status and price.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max rows (default 25)', default: 25 },
      },
    },
    execute: async ({ limit }) => fetchJson(`/api/admin/products?limit=${numberOr(limit, 25)}`),
  },
  {
    name: 'list_contracts',
    description: 'List smart contract listings with status and price.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max rows (default 25)', default: 25 },
      },
    },
    execute: async ({ limit }) => fetchJson(`/api/admin/contracts?limit=${numberOr(limit, 25)}`),
  },
  {
    name: 'list_bundles',
    description: 'List product + contract bundles with status.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max rows (default 25)', default: 25 },
      },
    },
    execute: async ({ limit }) => fetchJson(`/api/admin/bundles?limit=${numberOr(limit, 25)}`),
  },
  {
    name: 'get_pricing_overview',
    description: 'Fetch the pricing catalog — tiers, publish status, item counts.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max rows (default 100)', default: 100 },
      },
    },
    execute: async ({ limit }) => fetchJson(`/api/admin/pricing?limit=${numberOr(limit, 100)}`),
  },
]

interface ModelContextTool {
  name: string
  description: string
  inputSchema: AgentTool['inputSchema']
  execute: (args: Record<string, unknown>) => Promise<unknown>
}

interface ModelContext {
  registerTool?: (tool: ModelContextTool) => void | Promise<void>
  unregisterTool?: (name: string) => void | Promise<void>
}

function getModelContext(): ModelContext | null {
  try {
    const nav = navigator as unknown as { modelContext?: ModelContext }
    return nav.modelContext || null
  } catch {
    return null
  }
}

let registrationState: { registered: number; supported: boolean } | null = null

export function registerTools(): { registered: number; supported: boolean } {
  if (registrationState) return registrationState
  const ctx = getModelContext()
  if (!ctx?.registerTool) {
    registrationState = { registered: 0, supported: false }
    return registrationState
  }
  let count = 0
  for (const tool of TOOLS) {
    try {
      ctx.registerTool({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema,
        execute: tool.execute,
      })
      count++
    } catch {
      /* individual tool rejection — skip, keep going */
    }
  }
  registrationState = { registered: count, supported: true }
  return registrationState
}

export async function executeTool(
  name: string,
  args: Record<string, unknown> = {}
): Promise<{ success: boolean; data?: unknown; error?: string }> {
  const tool = TOOLS.find((t) => t.name === name)
  if (!tool) return { success: false, error: `Unknown tool: ${name}` }
  try {
    const data = await tool.execute(args)
    return { success: true, data }
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Tool failed' }
  }
}

export function describeToolsForPrompt(): string {
  return TOOLS.map((t) => {
    const argList = Object.entries(t.inputSchema.properties)
      .map(([k, v]) => `${k}: ${v.type}${v.default !== undefined ? ` = ${JSON.stringify(v.default)}` : ''}`)
      .join(', ')
    return `- ${t.name}(${argList}) — ${t.description}`
  }).join('\n')
}
